import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, CalendarIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { usePrompts, useDeletePrompt } from '../hooks/usePrompts';
import { useListState } from '../hooks/useListState';
import { useSortableData } from '../hooks/useSortableData';
import { SearchBar } from '../components/ui/SearchBar';
import { Pagination } from '../components/ui/Pagination';
import { ViewToggle } from '../components/ui/ViewToggle';
import { DataTable, DataTableHead, DataTableBody, DataTableRow, DataTableCell, TableHeader } from '../components/ui/DataTable';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import PromptCard from '../components/prompts/PromptCard';
import { useFlag } from '../hooks/useFeatureFlags';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';

export default function PromptsPage() {
  const navigate = useNavigate();
  const composerEnabled = useFlag('innovate.fe.composer.page');
  // The create affordance is gated by its manifest flag, whose
  // member-and-above cohorts carry the role rule (task 692).
  const canCreate = useFlag('innovate.fe.prompts.create');
  const { page, limit, search, viewMode, setPage, setLimit, setSearch, setViewMode } = useListState({
    entityType: 'prompts',
  });

  const { data, isLoading, error } = usePrompts(page, limit, { search: search || undefined });
  const prompts = data?.data || [];
  const pagination = data?.pagination;

  const { sortedData, sortConfig, handleSort } = useSortableData(prompts, 'updatedAt', 'desc');

  const deletePrompt = useDeletePrompt();

  const actions = canCreate ? (
    <Link
      to="/prompts/new"
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
    >
      <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
      Add Prompt
    </Link>
  ) : undefined;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">Error loading prompts. Please try again.</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Prompts"
        description={pagination ? `${pagination.total} ${pagination.total === 1 ? 'prompt' : 'prompts'}${search ? ' found' : ' total'}` : 'Prompt templates with Langfuse integration'}
        actions={actions}
      />

      <div className="mt-6">
        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search prompts..."
              />
            </div>
            <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading prompts...</div>
          </div>
        ) : sortedData.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400 mb-4">
              {search ? `No prompts found matching "${search}"` : 'No prompts found. Create your first prompt to get started.'}
            </div>
            {!search && canCreate && (
              <Link
                to="/prompts/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                Create your first prompt
              </Link>
            )}
            {!search && composerEnabled && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4" data-testid="prompts-empty-composer-hint">
                Composer assembles these for you when you describe what you want —{' '}
                <Link to="/composer" className="text-indigo-600 dark:text-indigo-400 hover:underline">
                  most people start there
                </Link>
                .
              </p>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {sortedData.map((prompt) => (
              <PromptCard key={prompt.id} prompt={prompt} />
            ))}
          </div>
        ) : (
          <DataTable>
            <DataTableHead>
              <SortableTableHeader
                label="Prompt"
                field="name"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-1/2 min-w-0"
              />
              <SortableTableHeader
                label="Status"
                field="isActive"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-24"
              />
              <SortableTableHeader
                label="Updated"
                field="updatedAt"
                sortField={sortConfig.field}
                sortDirection={sortConfig.direction}
                onSort={handleSort}
                className="w-32"
              />
              <TableHeader className="w-20">
                <span className="sr-only">Actions</span>
              </TableHeader>
            </DataTableHead>
            <DataTableBody>
              {sortedData.map((prompt) => (
                <DataTableRow
                  key={prompt.id}
                  onClick={() => navigate(`/prompts/${prompt.id}`)}
                >
                  <DataTableCell>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {prompt.name}
                      </div>
                      {prompt.content && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                          {prompt.content.length > 80 ? prompt.content.substring(0, 80) + '...' : prompt.content}
                        </div>
                      )}
                    </div>
                  </DataTableCell>
                  <DataTableCell>
                    {prompt.isActive === 'true' ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                        <XCircleIcon className="h-3 w-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {new Date(prompt.updatedAt).toLocaleDateString()}
                    </div>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/prompts/${prompt.id}`);
                        }}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                      >
                        View
                      </button>
                      {/* The one delete affordance (task 864). The inline
                          two-step Confirm/Cancel swap this replaces named
                          nothing that used the resource and offered no way
                          back from a mis-click. */}
                      <DeleteResourceControl
                        flag="innovate.fe.prompts.delete"
                        entity="prompts"
                        entityLabel="prompt"
                        name={prompt.name}
                        id={prompt.id}
                        stopPropagation
                        body={<p>Agents using it stop using it; nothing else about them changes.</p>}
                        onDelete={() => deletePrompt.mutateAsync(prompt.id)}
                      />
                    </div>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              pagination={pagination}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
